import type {Node as ProseMirrorNode, NodeType, ResolvedPos, Slice} from "@tiptap/pm/model";
import {dropPoint} from "@tiptap/pm/transform";
import type {EditorView} from "@tiptap/pm/view";
import {
    findPrintoutRegion,
    getPrintoutRegionKind,
    PRINTOUT_REGION_ATTRIBUTE,
} from "../../blocks/headerFooter/printoutRegionOptions";
import type {PrintoutRegionKind} from "../../blocks/headerFooter/printoutRegionOptions";

export interface PrintoutDropCoords {
    left: number;
    top: number;
}

export interface PrintoutDropTarget {
    from: number;
    to: number;
    column: number | null;
}

const COLUMN_SELECTOR = '[data-type="printout-container-column"]';
const CONTAINER_SELECTOR = '[data-type="printout-container"], [data-printout-block-type="printoutContainer"]';

// Text selections and pinned regions keep ProseMirror's native handling.
export const isPrintoutBlockSlice = (slice: Slice): boolean => {
    if (slice.openStart || slice.openEnd || !slice.content.childCount) return false;

    let blocksOnly = true;
    slice.content.forEach(node => {
        if (!node.isBlock || getPrintoutRegionKind(node.type.name)) blocksOnly = false;
    });

    return blocksOnly;
};

const resolveSelfNestingDepth = ($position: ResolvedPos, nodeType: NodeType): number | null => {
    for (let depth = 1; depth <= $position.depth; depth += 1) {
        if ($position.node(depth).type === nodeType) return depth;
    }

    return null;
};

// A column that accepts the block wins before an outer isolating boundary is considered.
const resolveBlockedIsolatingDepth = ($position: ResolvedPos, nodeType: NodeType): number | null => {
    let blockedDepth: number | null = null;

    for (let depth = $position.depth; depth > 0; depth -= 1) {
        const ancestor = $position.node(depth);
        const index = $position.index(depth);

        if (ancestor.canReplaceWith(index, index, nodeType)) break;
        if (ancestor.type.spec.isolating) blockedDepth = depth;
    }

    return blockedDepth;
};

// The caret path also uses this rule, without snapping text insertions to a block edge.
export const resolvePrintoutBlockEscapePosition = ($position: ResolvedPos, nodeType: NodeType): number | null => {
    const depths = [
        resolveSelfNestingDepth($position, nodeType),
        resolveBlockedIsolatingDepth($position, nodeType),
    ].filter((depth): depth is number => depth !== null);

    if (!depths.length) return null;

    const depth = Math.min(...depths);

    return getPrintoutRegionKind($position.node(depth).type.name) === "footer"
        ? $position.before(depth)
        : $position.after(depth);
};

const findPrintoutRegionDepth = ($position: ResolvedPos): number | null => {
    for (let depth = $position.depth; depth > 0; depth -= 1) {
        if (getPrintoutRegionKind($position.node(depth).type.name)) return depth;
    }

    return null;
};

const getPointedElement = (view: EditorView, coords: PrintoutDropCoords): Element | null => {
    const element = view.dom.ownerDocument.elementFromPoint(coords.left, coords.top);

    return element && view.dom.contains(element) ? element : null;
};

const getPointedPrintoutRegionKind = (element: Element | null): PrintoutRegionKind | null => {
    const kind = element?.closest(`[${PRINTOUT_REGION_ATTRIBUTE}]`)?.getAttribute(PRINTOUT_REGION_ATTRIBUTE);

    return kind === "header" || kind === "footer" ? kind : null;
};

const findPointedColumn = (element: Element | null, coords: PrintoutDropCoords): Element | null => {
    const column = element?.closest(COLUMN_SELECTOR);

    if (column) return column;

    const container = element?.closest(CONTAINER_SELECTOR);

    if (!container) return null;

    // The gutter belongs to the nearest column; it must not become a drop outside the container.
    let nearestColumn: Element | null = null;
    let nearestDistance = Infinity;

    container.querySelectorAll(COLUMN_SELECTOR).forEach(candidate => {
        if (candidate.closest(CONTAINER_SELECTOR) !== container) return;

        const rect = candidate.getBoundingClientRect();
        const distance = Math.max(rect.left - coords.left, coords.left - rect.right, 0);

        if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestColumn = candidate;
        }
    });

    return nearestColumn;
};

const resolveColumnPosition = (view: EditorView, column: Element, coords: PrintoutDropCoords): number => {
    const start = view.posAtDOM(column, 0);
    const node = view.state.doc.resolve(start).parent;
    let position = start + node.content.size;
    let found = false;

    // Hit the full column, including empty space, then choose between its direct children.
    node.forEach((_child, offset) => {
        if (found) return;

        const element = view.nodeDOM(start + offset);

        if (!(element instanceof HTMLElement)) return;

        const rect = element.getBoundingClientRect();

        if (coords.top < (rect.top + rect.bottom) / 2) {
            position = start + offset;
            found = true;
        }
    });

    return position;
};

// An absolutely positioned footer can be the coordinate hit for empty body space. Enter a region
// only when the pointer is actually on it and it accepts the entire payload without wrapping it.
const resolvePrintoutBodyPosition = (
    doc: ProseMirrorNode,
    position: number,
    slice: Slice,
    pointedRegionKind: PrintoutRegionKind | null,
): number => {
    const $position = doc.resolve(position);
    const regionDepth = findPrintoutRegionDepth($position);

    if (regionDepth !== null && pointedRegionKind) {
        const region = $position.node(regionDepth);
        const index = $position.index(regionDepth);

        if (getPrintoutRegionKind(region.type.name) === pointedRegionKind
            && region.canReplace(index, index, slice.content)) return position;
    }

    const header = findPrintoutRegion(doc, "header");
    const footer = findPrintoutRegion(doc, "footer");
    const bodyStart = header ? header.node.nodeSize : 0;
    const bodyEnd = footer ? footer.pos : doc.content.size;

    return Math.min(Math.max(position, bodyStart), bodyEnd);
};

const snapToHoveredBlockEdge = (
    view: EditorView,
    hit: {pos: number; inside: number},
    coords: PrintoutDropCoords,
): number => {
    if (hit.inside < 0) return hit.pos;

    const node = view.state.doc.nodeAt(hit.inside);
    const element = view.nodeDOM(hit.inside);

    if (!node?.isBlock || !(element instanceof HTMLElement)) return hit.pos;
    if (getPrintoutRegionKind(node.type.name)) return hit.pos;

    const rect = element.getBoundingClientRect();

    if (!rect.height) return hit.pos;

    return coords.top < (rect.top + rect.bottom) / 2 ? hit.inside : hit.inside + node.nodeSize;
};

/** Shared by palette insertion, existing-block moves/copies, and their preview line. */
export const resolvePrintoutBlockDropTarget = (
    view: EditorView,
    slice: Slice,
    coords: PrintoutDropCoords,
): PrintoutDropTarget | null => {
    const pointedElement = getPointedElement(view, coords);
    const pointedColumn = findPointedColumn(pointedElement, coords);
    const hit = pointedColumn ? null : view.posAtCoords(coords);

    if (!pointedColumn && !hit) return null;

    const {doc} = view.state;
    const bodyPosition = resolvePrintoutBodyPosition(
        doc,
        pointedColumn
            ? resolveColumnPosition(view, pointedColumn, coords)
            : snapToHoveredBlockEdge(view, hit!, coords),
        slice,
        getPointedPrintoutRegionKind(pointedElement),
    );
    const dropPosition = dropPoint(doc, bodyPosition, slice);

    if (dropPosition === null) return null;

    let position = dropPosition;

    // A closed selection may contain several blocks. None may nest inside its own node type.
    slice.content.forEach(node => {
        const escape = resolvePrintoutBlockEscapePosition(doc.resolve(position), node.type);
        if (escape !== null) position = escape;
    });

    let $position = doc.resolve(position);
    const parent = $position.parent;
    const column = parent.type.name === "printoutContainerColumn" ? $position.before() : null;
    let to = position;

    // An unused column contains one paragraph to host a caret. Replace it on the first drop,
    // wherever the pointer sits in the column, rather than retaining a blank row above the block.
    if (column !== null && parent.childCount === 1
        && parent.firstChild?.type.name === "paragraph" && parent.firstChild.content.size === 0) {
        position = $position.start();
        to = $position.end();
        $position = doc.resolve(position);
    }

    // dropPoint can propose wrapping when no direct insertion fits. Printout blocks never need it.
    if (!$position.parent.canReplace($position.index(), doc.resolve(to).index(), slice.content)) return null;

    return {from: position, to, column};
};
